// apps/web/src/pages/reservation/slot-step.tsx — Phase 03 plan 03-04,
// extended by Phase 4 plan 04-03 to hand off to the wizard-state
// pipeline.
//
// Guest Slot + Party-Size picker. Public route at /reservieren/zeit
// with query params `?date=YYYY-MM-DD&slot=<id>`. URL is reload-safe;
// an invalid date redirects to /reservieren/datum (replaces history so
// back returns to the date picker).
//
// Per 03-UI-SPEC: only bookable slots (remainingSeats > 0) reach
// the page (the server already filters them). If a refetch returns 0
// slots, the page clears the local selection and announces the
// staleCleared message in `role="alert"`. Per 260803-jsc the chip
// itself now also renders disabled if a 0-seat row slips past the
// server filter (defense in depth) — the chip stays in the grid as a
// non-clickable affordance, not a hidden row.
//
// Phase 4 added a party-size stepper on this page (the "A3" slot in
// the wizard's 6-step structure lives here too — there is no
// separate /reservieren/personen route). `confirmSelection` writes
// {serviceDate, partySize, selectedSlot} into the wizard zustand
// store and navigates to /reservierung/slot (A4). The URL `?slot=…`
// update is kept as a reload-safe hint; A4's bounce-back guard reads
// the wizard state, not the URL.

import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, Info, Minus, Plus } from "lucide-react";
import { gqlRequest } from "../../lib/api";
import { SLOTS_QUERY } from "./operations";
import {
  setPartySize,
  setSelectedSlot,
  setServiceDate,
  useWizardSlice,
} from "../../wizard/state";

// ===== Types =====

type Weekday1to7 = "ONE" | "TWO" | "THREE" | "FOUR" | "FIVE" | "SIX" | "SEVEN";

interface Slot {
  id: string;
  startTime: string;
  durationMinutes: number;
  startsAt: string;
  endsAt: string;
  remainingSeats: number;
  weekday: Weekday1to7;
}

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// ===== Helpers =====

function formatHHmm(s: string): string {
  // s is the canonical HH:mm wire value (e.g. "18:00").
  if (!/^\d{2}:\d{2}$/.test(s)) return s;
  return s;
}

function formatLongDate(iso: string, locale: "de" | "en"): string {
  if (!DATE_REGEX.test(iso)) return iso;
  const year = Number.parseInt(iso.slice(0, 4), 10);
  const month = Number.parseInt(iso.slice(5, 7), 10);
  const day = Number.parseInt(iso.slice(8, 10), 10);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return iso;
  }
  const dt = new Date(Date.UTC(year, month - 1, day));
  const weekday = dt.getUTCDay();
  const weekdayName =
    (locale === "en" ? WEEKDAY_SHORT_EN : WEEKDAY_SHORT_DE)[weekday] ?? "";
  const monthName =
    (locale === "en" ? MONTH_NAMES_EN : MONTH_NAMES_DE)[month - 1] ?? "";
  if (locale === "en") {
    return `${weekdayName}, ${monthName} ${day}, ${year}`;
  }
  return `${weekdayName}., ${day}. ${monthName} ${year}`;
}

const MONTH_NAMES_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const MONTH_NAMES_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAY_SHORT_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const WEEKDAY_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ===== Component =====

export function SlotStepPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const dateParam = params.get("date");
  const date = dateParam && DATE_REGEX.test(dateParam) ? dateParam : null;
  const slotParam = params.get("slot");

  const locale = i18n.language.startsWith("en") ? "en" : "de";

  // Party size is collected on A2 (this page) instead of a separate
  // A3 step. The wizard's default of 2 is fine for first-time visitors;
  // returning visitors get their last party-size from localStorage
  // (see wizard/state.ts loadInitial). The picker is also live-synced
  // into wizard state so A4's slots query can filter by `partySize`.
  const partySize = useWizardSlice((s) => s.partySize);

  const query = useQuery<{ slots: Slot[] }>({
    queryKey: ["slots", date],
    queryFn: async () => gqlRequest<{ slots: Slot[] }>(SLOTS_QUERY, { date }),
    enabled: date !== null,
    placeholderData: keepPreviousData,
  });

  const slots = useMemo(() => (query.data?.slots ?? []).slice(), [query.data]);

  // Local selection state — synced from the URL but not two-way so a
  // refetch that returns 0 slots can clear it locally without
  // re-validating the URL.
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(
    slotParam,
  );
  const [staleVisible, setStaleVisible] = useState(false);

  // Keep the local selection aligned with the URL when the user
  // navigates back from the date step.
  useEffect(() => {
    setSelectedSlotId(slotParam);
  }, [slotParam]);

  // If the selected slot disappeared after a refetch, clear it and
  // surface the staleCleared alert. The page stays on /zeit.
  useEffect(() => {
    if (
      selectedSlotId !== null &&
      query.data &&
      !slots.some((s) => s.id === selectedSlotId)
    ) {
      setSelectedSlotId(null);
      setStaleVisible(true);
    }
  }, [query.data, slots, selectedSlotId]);

  // Invalid date → redirect to date-step. Done after all hooks so the
  // rules-of-hooks lint rule is satisfied (hooks must run in the
  // same order on every render, regardless of early-return paths).
  if (!date) {
    return <Navigate to="/reservieren/datum" replace />;
  }

  function selectSlot(id: string) {
    setSelectedSlotId(id);
  }

  function confirmSelection() {
    if (!selectedSlotId || !date) return;
    const sp = new URLSearchParams(params);
    sp.set("date", date);
    sp.set("slot", selectedSlotId);
    // Use replace so back doesn't return to the un-slotted URL.
    setParams(sp, { replace: true });

    // Hand the selection off to the Phase 4 wizard state so the next
    // steps (A4 slot re-pick, A5 guest info, A6 summary, A7 confirm)
    // can read date + party + slot without re-deriving from URL.
    // The URL stays as a reload-safe fallback for A2 → A3, but A4's
    // bounce-back guard reads from wizard state, not URL — so this
    // write is required, not optional.
    setServiceDate(date);
    setPartySize(partySize);
    const slot = slots.find((s) => s.id === selectedSlotId);
    if (slot) {
      setSelectedSlot({
        slotDefinitionId: slot.id,
        startsAt: slot.startsAt,
        endsAt: slot.endsAt,
        durationMinutes: slot.durationMinutes,
        startTime: slot.startTime,
        remainingSeats: slot.remainingSeats,
      });
    }
    navigate(`/reservierung/slot?date=${encodeURIComponent(date)}`);
  }

  function goBack() {
    const fromState = (location.state ?? {}) as {
      from?: { month?: string; state?: string };
    };
    const sp = new URLSearchParams();
    if (fromState.from?.month) sp.set("month", fromState.from.month);
    if (fromState.from?.state) sp.set("state", fromState.from.state);
    const qs = sp.toString();
    navigate(`/reservieren/datum${qs ? `?${qs}` : ""}`);
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="slot-step-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
          data-testid="slot-header"
        >
          <button
            type="button"
            onClick={goBack}
            aria-label={t("availability.slot.back")}
            className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="slot-back"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <h1
            className="font-heading text-2xl md:text-3xl flex-1"
            data-testid="slot-heading"
          >
            {t("availability.slot.heading")}
          </h1>
          <button
            type="button"
            onClick={() =>
              void i18n.changeLanguage(locale === "en" ? "de" : "en")
            }
            aria-label="toggle-language"
            data-testid="slot-lang-toggle"
            className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full border border-divider font-heading text-sm hover:bg-surface"
          >
            {locale === "en" ? "EN" : "DE"}
          </button>
        </header>

        <div className="px-4 pt-4">
          <p
            className="text-xs text-text/70 mb-2"
            data-testid="slot-wizard-meta"
          >
            {t("availability.slot.wizardMeta")}
          </p>
          <WizardProgress current={2} />
        </div>

        <div className="px-4 pt-4">
          <p
            className="font-heading text-2xl text-text"
            data-testid="slot-date-label"
          >
            {formatLongDate(date, locale)}
          </p>
        </div>

        <div className="px-4 pt-4" data-testid="slot-party-size-panel">
          <p
            className="text-sm text-text/70 mb-2"
            data-testid="slot-party-size-label"
          >
            {t("availability.slot.partySizeLabel", {
              defaultValue: "Personen",
            })}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setPartySize(Math.max(1, Math.min(12, partySize - 1)))
              }
              disabled={partySize <= 1}
              aria-label={t("availability.slot.partySizeDec", {
                defaultValue: "Eine Person weniger",
              })}
              data-testid="slot-party-size-dec"
              className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-md border border-divider text-text disabled:text-text/30 disabled:cursor-not-allowed hover:bg-surface"
            >
              <Minus className="h-4 w-4" aria-hidden="true" />
            </button>
            <span
              className="font-heading text-2xl tabular-nums w-10 text-center"
              data-testid="slot-party-size-value"
            >
              {partySize}
            </span>
            <button
              type="button"
              onClick={() =>
                setPartySize(Math.max(1, Math.min(12, partySize + 1)))
              }
              disabled={partySize >= 12}
              aria-label={t("availability.slot.partySizeInc", {
                defaultValue: "Eine Person mehr",
              })}
              data-testid="slot-party-size-inc"
              className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-md border border-divider text-text disabled:text-text/30 disabled:cursor-not-allowed hover:bg-surface"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {staleVisible && (
          <div
            role="alert"
            className="mx-4 mt-3 text-sm text-text bg-surface border border-divider rounded-md p-3"
            data-testid="slot-stale-cleared"
          >
            {t("availability.slot.staleCleared")}
          </div>
        )}

        <div
          className="px-4 pt-4 pb-6"
          aria-busy={query.isLoading || undefined}
          data-testid="slot-grid-panel"
        >
          {query.isLoading ? (
            <SlotSkeleton />
          ) : query.isError ? (
            <ErrorState
              message={t("availability.calendar.error")}
              onRetry={() => void query.refetch()}
              retryLabel={t("availability.calendar.retry")}
            />
          ) : slots.length === 0 ? (
            <EmptyState
              heading={t("availability.slot.empty")}
              body={t("availability.slot.emptyBody")}
            />
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
              {slots.map((s) => (
                <SlotButton
                  key={s.id}
                  slot={s}
                  selected={selectedSlotId === s.id}
                  onSelect={() => selectSlot(s.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div
          role="note"
          className="mx-4 mb-4 flex items-start gap-2 text-sm text-text/80 bg-surface border border-divider rounded-md p-3"
          data-testid="slot-cutoff-hint"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{t("availability.slot.cutoffHint")}</span>
        </div>

        <div className="px-4 pb-6">
          <button
            type="button"
            onClick={confirmSelection}
            disabled={selectedSlotId === null}
            aria-disabled={selectedSlotId === null}
            data-testid="slot-cta"
            className={`w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
              selectedSlotId === null
                ? "border-divider text-text/40 cursor-not-allowed"
                : "border-accent text-accent-700 hover:bg-accent-100"
            }`}
          >
            <span>{t("availability.slot.cta")}</span>
          </button>
        </div>
      </div>
    </main>
  );
}

// ===== Sub-components =====

function WizardProgress({ current }: { current: number }) {
  const { t } = useTranslation();
  return (
    <ol
      role="list"
      aria-label={t("reservation.wizard.progressAria", {
        current,
        total: 6,
      })}
      className="flex gap-1 mb-4"
      data-testid="wizard-progress"
      data-wizard-current={current}
    >
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <li
          key={n}
          aria-current={n === current ? "step" : undefined}
          className={`h-[3px] flex-1 rounded-sm ${
            n <= current ? "bg-accent" : "bg-divider"
          }`}
          data-testid={`wizard-segment-${n}`}
          data-filled={n <= current}
        />
      ))}
    </ol>
  );
}

function SlotButton({
  slot,
  selected,
  onSelect,
}: {
  slot: Slot;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const time = formatHHmm(slot.startTime);
  const isFull = slot.remainingSeats === 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isFull}
      aria-disabled={isFull}
      aria-pressed={selected}
      aria-label={t("availability.slot.cta") + ` · ${time}`}
      data-testid={`slot-button-${slot.id}`}
      data-selected={selected}
      data-time={time}
      data-disabled-zero-seats={isFull}
      className={`min-h-[44px] flex items-center justify-center rounded-md border px-1 py-2 tabular-nums lining-nums ${
        isFull
          ? "border-divider text-text/40 cursor-not-allowed"
          : selected
            ? "border-accent text-accent-700 bg-accent-100"
            : "border-divider text-text hover:bg-surface"
      }`}
    >
      <span className="font-medium text-sm leading-none">{time}</span>
    </button>
  );
}

function SlotSkeleton() {
  const { t } = useTranslation();
  return (
    <div data-testid="slot-skeleton">
      <div className="grid grid-cols-3 gap-2" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="min-h-[44px] rounded-md border border-divider bg-surface/60"
          />
        ))}
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {t("availability.calendar.loading")}
      </p>
    </div>
  );
}

function EmptyState({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="py-6 text-center" data-testid="slot-empty">
      <p className="font-heading text-xl mb-2">{heading}</p>
      <p className="text-sm text-text/70">{body}</p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <div role="alert" className="py-4" data-testid="slot-error">
      <p className="text-sm text-text mb-3">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="btn btn-secondary"
        data-testid="slot-retry"
      >
        {retryLabel}
      </button>
    </div>
  );
}
